import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { bindActionCreators } from "redux";
import { connect, type ConnectedProps } from "react-redux";
import isEmpty from "lodash/isEmpty";

import { FlowFilterForm } from "../../../../components/filterForms";
import { SearchInput } from "../../../../components";
import ItemsView from "views/ItemsView/ItemsView";
import { ContentTypesEnum, ItemsTypes, RolePermissions, SortingDirection } from "../../../../enums";
import { bindAction } from "../../../../interfaces";
import { CreateFlowButton } from "./FlowsOverviewHeader/CreateFlowButton/CreateFlowButton";
import { ContentType } from "../../../../features/Library/PeopleAssignments/types";
import { type AppDispatch, type RootState } from "../../../../features/Application/globaltypes/redux";
import { type DropdownProps } from "semantic-ui-react";
import { type IFlowAssignmentModelItem } from "../../../../features/People/types";
import { FlowLockSuccess } from "../../../../features/Application/services/realTimeNotification/events/library/libraryEvents";
import { reset } from "../../../../features/Library/Flows/state/slices/flowOverviewSlice";
import { resetFlowBaseEntity } from "../../../../features/Library/Flows/state/slices/flowBaseSlice";
import { type FlowOverview, type FlowFiltersEnum } from "../../../../features/Library/Flows/types/models";
import { flowsFilterSelector, flowsStateSelector } from "../../../../features/Library/Flows/state/selectors";
import { type CardsViewerItem } from "../../../../components/cardsViewer/types";
import { OverviewCard } from "./Card/OverviewCard";
import { HandleOnSelectionChanged, type SelectionChangedArgs } from "../../../../interfaces/onSelectionChanged";
import {
  changeVisibility,
  deleteFlows,
  duplicateFlowsV2,
  fetchOverviewFlows,
} from "../../../../features/Library/Flows/state/thunks/flowOverviewThunk";
import { getOverviewFilterOptions } from "../../../../features/Library/Flows/state/thunks/flowFiltersThunk";
import { type GenericFiltersMap } from "../../../../utils/filterUtils";
import { useFlowsQueryParams } from "./hooks/useFlowsQueryParams";

import Observable from "../../../../utils/Observable";
import ViewType from "../../../../enums/ViewType";
import FlowsNoResults from "./FlowsNoResults";
import SortOptions from "../../../../enums/SortOptions";
import sortOptions from "./sortOptions";
import { getColumnOptions, ColumnToParamMap, type Columns } from "./columnOptions";
import FlowRow from "./FlowRow";
import DeleteContentConfirmationModal from "../../../../components/deleteContentConfirmationModal/DeleteContentConfirmationModal";
import { useHasPermissions } from "../../../../features/Application/hooks";
import { withRouter, type WithRouterProps } from "../../../../adapters/withRouter/withRouter";

import FlowsOverviewHeader from "./FlowsOverviewHeader/FlowsOverviewHeader";

import * as flowEntityStateActions from "../../../../features/Library/Flows/state/actions/flowEntityStateActionCreators";
import { useRtn } from "hooks/useRtn";
import { type SelectionContext } from "./types";

import "./flowsOverview.scss";
import { FeatureFlags } from "featureFlags";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FlowsVisibilityModal } from "./FlowsOverviewHeader/FlowsVisibility/FlowsVisibilityModal";

const DEFAULT_PARAMS = {
  orderBy: SortOptions.ModifiedDateDesc.split(" ") as [string, string],
  column: "modified",
  direction: SortingDirection.Descending,
  pageSize: 10,
  gridPageSize: 30,
  skip: 0,
  flowLockSuccess: [FlowLockSuccess],
};

const defaultSelectionCtx: SelectionContext = {
  drafts: 0,
  unpublished: 0,
  purchased: 0,
  undeletable: 0,
  withPack: 0,
  hidden: 0,
  shown: 0,
};

export type FlowsOverviewProps = PropsFromRedux & WithRouterProps;

export const FlowsOverview: FC<FlowsOverviewProps> = (props) => {
  const {
    navigate,
    resetOverviewFlows,
    fetchOverviewFlows,
    flows,
    flowEntityStateActions,
    filterOptions,
    isFilterLoading,
    getFilterOptions,
    duplicateFlows,
    deleteFlows,
    resetFlowsItems,
  } = props;

  const {
    filters,
    search,
    page,
    pageSize,
    sortBy,
    sortDirection,
    setSearch,
    setFilters,
    resetFilters,
    setSort,
    setPagination,
    sortingColumnName: currentSortingColumnName,
  } = useFlowsQueryParams();

  const flowMigrationEnabled = useFeatureFlag(FeatureFlags.FlowVersioningMigration);
  const reloadListItemsRef = useRef<((enableSorting: boolean) => void) | undefined>(undefined);
  const selectedFlows = useRef(new Map());
  const hasPermissions = useHasPermissions([RolePermissions.FlowsCreate, RolePermissions.FlowsManage]);
  const searchWasJustChangedRef = useRef(false);
  const preventInitialReloadRef = useRef(true);

  const [selectedFlowIds, setSelectedFlowIds] = useState<number[]>([]);
  const [selectedViewType, setSelectedViewType] = useState(ViewType.GRID);
  const [orderBy, setOrderBy] = useState<[string, string]>(["", ""]);
  const [selectionCtx, setSelectionCtx] = useState<SelectionContext>(defaultSelectionCtx);

  const isGridView = useMemo(() => selectedViewType === ViewType.GRID, [selectedViewType]);
  const onTriggerFlowRemovalObserver = useMemo(() => new Observable(), []);
  const onTriggerChangeVisibilityObserver = useMemo(
    () => new Observable<(onConfirm: () => void, hidden: boolean, ids: number[]) => void>(),
    [],
  );

  const goToEditEntity = useCallback(
    (payload: { id: number }) => navigate(`${payload.id.toString()}/configuration`),
    [navigate],
  );

  useRtn(DEFAULT_PARAMS.flowLockSuccess, goToEditEntity);

  // Reload list when search changes
  useEffect(() => {
    if (preventInitialReloadRef.current) {
      preventInitialReloadRef.current = false;
      return;
    }
    if (!isGridView) {
      reloadListItemsRef.current?.(true);
    }
  }, [search, isGridView]);

  useEffect(() => {
    return () => {
      resetFlowsItems();
    };
  }, [resetFlowsItems]);

  const onSortChange = (_ev: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    resetOverviewFlows();
    const dataString = data.value as string;
    setOrderBy(dataString.split(" ") as [string, string]);
    setSelectedFlowIds([]);
    selectedFlows.current = new Map();

    const [column, direction] = dataString.split(" ");
    const sortDirectionValue = direction === "asc" ? SortingDirection.Ascending : SortingDirection.Descending;
    setSort(column, sortDirectionValue);
    loadFlowCards(undefined, undefined, column, direction);
  };

  const onClearedSelection = useCallback(() => {
    setSelectionCtx({ ...defaultSelectionCtx });
    selectedFlows.current = new Map();
    setSelectedFlowIds([]);
  }, []);

  const onSelectionChanged = (args: SelectionChangedArgs<FlowOverview>) => {
    const newSelectionCtx = { ...defaultSelectionCtx };
    const onAdded = (flow: FlowOverview) => {
      !flow.isEditable && ++newSelectionCtx.purchased;
      !flow.canBeDeleted && ++newSelectionCtx.undeletable;
      !flow.published && ++newSelectionCtx.drafts;
      !flow.hasBeenPublished && ++newSelectionCtx.unpublished;
      !!flow.dependencies?.packsCount && ++newSelectionCtx.withPack;
      flow.hidden && ++newSelectionCtx.hidden;
      !flow.hidden && ++newSelectionCtx.shown;

      selectedFlows.current.set(flow.id, {
        id: flow.id,
        title: flow.title,
        thumbnailUrl: flow.thumbnailUrl ?? null,
        published: flow.published,
        hasBeenPublished: flow.hasBeenPublished,
        canBeDeleted: flow.canBeDeleted,
        isEditable: flow.isEditable,
        canAutoStart: flow.canAutoStart,
        contentType: ContentType.Flow,
      });
    };

    const onRemoved = (flow: FlowOverview) => {
      !flow.isEditable && --newSelectionCtx.purchased;
      !flow.canBeDeleted && --newSelectionCtx.undeletable;
      !flow.published && --newSelectionCtx.drafts;
      !flow.hasBeenPublished && --newSelectionCtx.unpublished;
      !!flow.dependencies?.packsCount && --newSelectionCtx.withPack;
      flow.hidden && --newSelectionCtx.hidden;
      !flow.hidden && --newSelectionCtx.shown;
      selectedFlows.current.delete(flow.id);
    };

    HandleOnSelectionChanged(args, onAdded, onRemoved, onClearedSelection);
    setSelectionCtx({
      drafts: selectionCtx.drafts + newSelectionCtx.drafts,
      undeletable: selectionCtx.undeletable + newSelectionCtx.undeletable,
      purchased: selectionCtx.purchased + newSelectionCtx.purchased,
      unpublished: selectionCtx.unpublished + newSelectionCtx.unpublished,
      withPack: selectionCtx.withPack + newSelectionCtx.withPack,
      hidden: selectionCtx.hidden + newSelectionCtx.hidden,
      shown: selectionCtx.shown + newSelectionCtx.shown,
    });
  };

  const onSelectedFlowsChanged = (ids: number[]) => {
    setSelectedFlowIds(ids);
  };

  const listViewSort = useMemo(
    () => ({
      modified: () => {
        const [column, direction] = SortOptions.ModifiedDateDesc.split(" ");
        setOrderBy([column, direction]);
        return [column, direction];
      },
      added: () => {
        const [column, direction] = SortOptions.CreatedDateDesc.split(" ");
        setOrderBy([column, direction]);
        return [column, direction];
      },
      title: () => {
        const [column, direction] = SortOptions.Title.split(" ");
        setOrderBy([column, direction]);
        return [column, direction];
      },
      status: () => {
        const [column, direction] = DEFAULT_PARAMS.orderBy;
        setOrderBy([column, direction]);
        return [column, direction];
      },
      objective: () => {
        const [column, direction] = DEFAULT_PARAMS.orderBy;
        setOrderBy([column, direction]);
        return [column, direction];
      },
      noSort: () => {
        setOrderBy(["", ""]);
        return [undefined, undefined];
      },
      "": () => {
        const [column, direction] = DEFAULT_PARAMS.orderBy;
        setOrderBy([column, direction]);
        return [column, direction];
      },
    }),
    [],
  );

  const shouldSort = !searchWasJustChangedRef.current;

  const onViewTypeChange = (viewType: ViewType) => {
    resetOverviewFlows();
    onClearedSelection();
    setSelectedViewType(viewType);
    let column;
    let direction;
    const columnName = currentSortingColumnName?.toLowerCase() ?? "";
    if (sortBy) {
      [column, direction] = listViewSort[columnName as keyof typeof listViewSort]?.() ?? listViewSort[""]();
    } else {
      [column, direction] = listViewSort["noSort" as keyof typeof listViewSort]();
    }

    viewType === ViewType.GRID && loadFlowCards(undefined, undefined, column, direction);
  };

  const loadFlowList = (skip?: number, top?: number, sortingColumnName?: string, sortingDir?: SortingDirection) => {
    const trimmedSearch = search.trim();
    const normalizedColumnName = sortingColumnName?.toLowerCase();
    const shouldIgnoreSort = searchWasJustChangedRef.current && trimmedSearch.length > 0;

    const resolvedOrderProp =
      !shouldIgnoreSort && normalizedColumnName
        ? ColumnToParamMap[normalizedColumnName as keyof typeof ColumnToParamMap]
        : undefined;

    const orderProp = shouldIgnoreSort ? undefined : resolvedOrderProp;
    const nextPageSize = top ?? pageSize;
    const nextPage = nextPageSize ? Math.floor((skip ?? 0) / nextPageSize) + 1 : page;

    if (nextPageSize && (nextPage !== page || nextPageSize !== pageSize)) {
      setPagination(nextPage, nextPageSize);
    }

    if (!shouldIgnoreSort && orderProp && sortingDir) {
      setSort(orderProp, sortingDir);
    }

    fetchOverviewFlows({
      skip,
      top,
      sortBy: ColumnToParamMap[sortingColumnName as Lowercase<Columns>],
      sortDirection: sortingDir,
      searchText: search,
      filters,
    });
    searchWasJustChangedRef.current = false;
  };

  const loadFlowCards = useCallback(
    (
      skip?: number,
      top?: number,
      sortByParam?: string,
      sortDirectionParam?: string,
      overrideFilters?: GenericFiltersMap<FlowFiltersEnum>,
      searchTextOverride?: string,
    ) => {
      fetchOverviewFlows({
        skip,
        top,
        sortBy: sortByParam,
        sortDirection: sortDirectionParam,
        append: true,
        searchText: searchTextOverride ?? search,
        filters: overrideFilters ?? filters,
      });
      searchWasJustChangedRef.current = false;
    },
    [fetchOverviewFlows, search, filters],
  );

  const reloadItems = () => {
    let sort: string | undefined;
    const columnName = currentSortingColumnName?.toLowerCase() ?? "";

    if (columnName === undefined) {
      sort = undefined;
    } else if (shouldSort) {
      sort = columnName === "" ? "added" : columnName;
    } else {
      sort = "";
    }

    if (selectedViewType === ViewType.GRID) {
      if (orderBy[0] !== "" && orderBy[1] !== "") {
        [sort] = orderBy;
      }
      resetOverviewFlows();
      loadFlowCards(DEFAULT_PARAMS.skip, DEFAULT_PARAMS.gridPageSize, sort, sortDirection);
    } else {
      const paginationPresent = page ?? DEFAULT_PARAMS.skip;
      const resultsPerPagePresent = pageSize ?? DEFAULT_PARAMS.pageSize;
      const skip = (paginationPresent - 1) * resultsPerPagePresent;
      loadFlowList(skip, resultsPerPagePresent, sort, sortDirection);
    }
  };

  const loadFlows = (
    skip: number = DEFAULT_PARAMS.skip,
    top: number = DEFAULT_PARAMS.pageSize,
    sortingColumn?: string,
    sortingDirection?: SortingDirection,
  ) => {
    if (isGridView) {
      let [cardColumn, cardDirection] = orderBy;

      if (shouldSort && cardColumn === "") {
        [cardColumn, cardDirection] = DEFAULT_PARAMS.orderBy;
        setOrderBy(DEFAULT_PARAMS.orderBy);
      } else if (!shouldSort) {
        cardColumn = "";
        cardDirection = "";
      }
      loadFlowCards(flows.items.length, undefined, cardColumn, cardDirection);
    } else {
      let column, direction;
      if (shouldSort) {
        column = sortingColumn;
        direction = sortingDirection;
      } else {
        column = undefined;
        direction = undefined;
      }
      loadFlowList(skip, top, column, direction);
    }
  };

  const handleEditClick = (id: number, isDraft?: boolean) => () => {
    if (isDraft) {
      flowEntityStateActions.fetchDraftFlowEntity(id);
    } else {
      flowEntityStateActions.useExistingDraftFlowEntity(navigate, id);
    }
  };

  // @ts-ignore need to figure it out, why we don't provide props here
  const getFilterForm = () => <FlowFilterForm />;

  const handleApplyFilter = useCallback(
    (nextFilters: GenericFiltersMap<FlowFiltersEnum>) => {
      onClearedSelection();
      setFilters(nextFilters);

      if (selectedViewType === ViewType.GRID) {
        resetOverviewFlows();
        const [column, direction] = orderBy;
        loadFlowCards(undefined, undefined, column, direction, nextFilters);
      }
    },
    [setFilters, selectedViewType, resetOverviewFlows, orderBy, loadFlowCards, onClearedSelection],
  );

  const handleResetFilters = useCallback(() => {
    onClearedSelection();
    resetFilters();
    if (selectedViewType === ViewType.GRID) {
      resetOverviewFlows();
      const [column, direction] = orderBy;
      loadFlowCards(undefined, undefined, column, direction, {});
    }
  }, [resetFilters, selectedViewType, resetOverviewFlows, orderBy, loadFlowCards, onClearedSelection]);

  const handleDuplicateClick = (ids: number[]) => () => {
    onClearedSelection();
    duplicateFlows(ids, reloadItems);
  };

  const handleDeleteClick = (ids: number[], packsCount?: number) => () => {
    onTriggerFlowRemovalObserver.notify(
      () => {
        onClearedSelection();
        deleteFlows(ids, reloadItems);
      },
      {
        selectedItemsCount: ids.length,
        packsCount,
      },
    );
  };

  const handleToggleVisibility = (ids: number[], hidden: boolean) => () => {
    onTriggerChangeVisibilityObserver.notify(
      () => {
        props.toggleVisibility(ids, hidden, reloadItems);
      },
      hidden,
      ids,
    );
  };

  const createReloadListMethod = (reloadListItems: (enableSorting: boolean) => void) => {
    reloadListItemsRef.current = reloadListItems;
  };

  const onSortingColumnNameChange = (name: string) => {
    const normalizedName = name.toLowerCase();
    const orderProp = ColumnToParamMap[normalizedName as keyof typeof ColumnToParamMap];
    if (orderProp && sortDirection) {
      setSort(orderProp, sortDirection);
    }
  };
  const onSortingDirectionChange = (direction: SortingDirection) => {
    if (sortBy) {
      setSort(sortBy, direction);
    }
  };

  const refetchGridOrListItemsFromStart = useCallback(
    async (
      skip?: number,
      top?: number,
      sortByParam?: string,
      sortDirectionParam?: string,
      searchTextOverride?: string,
    ) => {
      if (isGridView) {
        resetOverviewFlows();
        loadFlowCards(skip, top, sortByParam, sortDirectionParam, undefined, searchTextOverride);
      } else {
        reloadListItemsRef.current?.(isEmpty(searchTextOverride ?? search));
      }
      searchWasJustChangedRef.current = false;
    },
    [isGridView, loadFlowCards, resetOverviewFlows, search],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      searchWasJustChangedRef.current = true;

      // Allows table header to update before fetching data
      setTimeout(() => {
        const columnName = currentSortingColumnName?.toLowerCase() ?? "";
        if (value === "") {
          const [column, direction] = listViewSort[columnName as keyof typeof listViewSort]?.() ?? listViewSort[""]();
          refetchGridOrListItemsFromStart(undefined, undefined, column, direction, value);
        } else {
          refetchGridOrListItemsFromStart(undefined, undefined, undefined, undefined, value);
        }
      }, 0);
    },
    [setSearch, currentSortingColumnName, listViewSort, refetchGridOrListItemsFromStart],
  );

  const viewFlows = useMemo(() => {
    return flows.items.map((flow: FlowOverview) => ({
      ...flow,
      readonly: !hasPermissions,
      isChecked: selectedFlowIds.includes(flow.id),
      restrictDeletion: true,
    }));
  }, [flows.items, hasPermissions, selectedFlowIds]);

  return (
    <section className="nested-content flows">
      <FlowsOverviewHeader
        selectedIds={selectedFlowIds}
        selectionCtx={selectionCtx}
        flowMap={selectedFlows.current}
        onDuplicateFlows={handleDuplicateClick}
        flowDeletionHandler={handleDeleteClick}
        setSelectedIds={onSelectedFlowsChanged}
        clearSelection={onClearedSelection}
        onToggleVisibility={handleToggleVisibility}
      />
      <ItemsView
        className="alignment-padding"
        blur
        paginateOnLoad
        viewType={selectedViewType}
        columnOptions={getColumnOptions()}
        noResultsContent={
          <FlowsNoResults byCriteria={!isEmpty(filters) || !!search} createFlowButton={<CreateFlowButton />} />
        }
        sortOptions={sortOptions}
        onSortChange={onSortChange}
        getData={loadFlows}
        itemsInlineCount={flows.itemsCount}
        isLoading={flows.isLoading}
        items={viewFlows}
        itemsType={ItemsTypes.Flow}
        buildTableBody={(flow: IFlowAssignmentModelItem) =>
          FlowRow({
            flow,
            hasPermission: hasPermissions,
            buttonHandlers: {
              handleEditClick,
              handleDuplicateClick,
              handleDeleteClick,
              handleToggleVisibility,
            },
            isDuplicateDisabled: flowMigrationEnabled,
          })
        }
        renderFilterForm={getFilterForm}
        filterOptions={filterOptions}
        resetFilter={handleResetFilters}
        appliedFilter={filters}
        filterOptionsLoading={isFilterLoading}
        applyFilter={handleApplyFilter}
        getFilterOptions={getFilterOptions}
        onSelectedListItemsChanged={onSelectedFlowsChanged}
        onSelectedItemsChanged={onSelectedFlowsChanged}
        renderCard={(props: CardsViewerItem<FlowOverview>) => {
          return (
            <OverviewCard
              {...props}
              {...{
                onEdit: handleEditClick,
                onCopy: handleDuplicateClick,
                onDelete: handleDeleteClick,
                onToggleVisibility: handleToggleVisibility,
              }}
              readonly={!hasPermissions}
            />
          );
        }}
        hasCardURL
        selectedIds={selectedFlowIds}
        isAllDataLoaded={flows.areAllLoaded}
        onViewTypeChange={onViewTypeChange}
        orderBy={shouldSort ? orderBy.join(" ") ?? DEFAULT_PARAMS.orderBy.join(" ") : ""}
        sortingDirection={shouldSort ? sortDirection ?? DEFAULT_PARAMS.direction : ""}
        sortingColumnName={shouldSort ? currentSortingColumnName : ""}
        onSelectionChanged={onSelectionChanged}
        onLoad={selectedViewType === ViewType.GRID ? loadFlows : undefined}
        renderSearch={() => (
          <SearchInput placeholder="Search for Flows..." onChange={handleSearchChange} defaultValue={search} />
        )}
        isSelectDisabled={() => !hasPermissions}
        setReloadListItems={createReloadListMethod}
        permissions={[RolePermissions.FlowsManage]}
        pagination={page}
        setSortingColumnName={onSortingColumnNameChange}
        setSortingDirection={onSortingDirectionChange}
      />

      <DeleteContentConfirmationModal
        contentType={ContentTypesEnum.Flows}
        onTriggerRemoveContentObserver={onTriggerFlowRemovalObserver}
        onClose={onClearedSelection}
      />

      <FlowsVisibilityModal observer={onTriggerChangeVisibilityObserver} onClose={onClearedSelection} />
    </section>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const flows = flowsStateSelector(state);
  return {
    flows: flows.overview.flowOverview,
    filterOptions: flowsFilterSelector(state).filterOptions,
    isFilterLoading: flowsFilterSelector(state).isLoading,
    isLoading: flows.base.entity.changingEntityState,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    getFilterOptions: bindAction(getOverviewFilterOptions, dispatch),
    flowEntityStateActions: bindActionCreators(flowEntityStateActions, dispatch),
    fetchOverviewFlows: bindAction(fetchOverviewFlows, dispatch),
    resetOverviewFlows: bindAction(reset, dispatch),
    resetFlowBaseEntity: bindAction(resetFlowBaseEntity, dispatch),
    duplicateFlows: bindAction(duplicateFlowsV2, dispatch),
    deleteFlows: bindAction(deleteFlows, dispatch),
    resetFlowsItems: bindAction(reset, dispatch),
    toggleVisibility: bindAction(changeVisibility, dispatch),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(withRouter(FlowsOverview));
export default ConnectedComponent;
